import React from 'react';
import { UserRole } from '@/types/index';
import { useAuth } from '@hooks/useAuth';

interface PlanGateProps {
  /** Rôle minimum requis pour voir le contenu */
  minRole: UserRole;
  /** Contenu affiché si le rôle est suffisant */
  children: React.ReactNode;
  /** Contenu affiché si le rôle est insuffisant (optionnel, sinon null) */
  fallback?: React.ReactNode;
}

const PLAN_LABELS: Record<UserRole, string> = {
  FREEMIUM: 'Freemium',
  PREMIUM: 'Premium',
  PRO: 'Pro',
  ADMIN: 'Admin',
};

/**
 * Affiche `children` uniquement si l'utilisateur a le rôle minimum requis.
 * Sinon, affiche `fallback` (ou un badge "Plan X requis" par défaut).
 *
 * Exemple :
 *   <PlanGate minRole="PRO">
 *     <BankDossierButton />
 *   </PlanGate>
 */
export const PlanGate: React.FC<PlanGateProps> = ({ minRole, children, fallback }) => {
  const { hasMinRole } = useAuth();

  if (hasMinRole(minRole)) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return (
    <span
      title={`Cette fonctionnalité nécessite le plan ${PLAN_LABELS[minRole]}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 cursor-not-allowed select-none"
    >
      🔒 Plan {PLAN_LABELS[minRole]}
    </span>
  );
};
